import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { getErrorMessage } from "@/utils/withErrorHandling";
import LocationPickerMap from "@/components/admin/LocationPickerMap";
import PullStationWizard from "@/components/wind/PullStationWizard";
import {
  CreatePushStationRequest,
  CredentialMode,
  MS_TO_KNOTS,
  PullTestResult,
  PushReject,
  PushStationCredential,
  PushStationService,
  SnapshotStation,
  StationContact,
  ORGANIZATION_TYPES,
  STATION_TYPES,
} from "@/services/PushStationService";
import { describeErrorCode, maskUrl, summarizeMapping } from "@/utils/jsonMapping";
import { paginate, sortRows, SortAccessors, useTableState } from "@/hooks/useTableState";
import Pagination from "@/components/common/Pagination";
import SortableHeader from "@/components/common/SortableHeader";
import FilterSelect from "@/components/common/FilterSelect";
import SearchInput from "@/components/common/SearchInput";
import TableSkeleton from "@/components/common/TableSkeleton";
import ActionsMenu, { ActionItem } from "@/components/common/ActionsMenu";
import SidePanel, { DetailRow, DetailSection } from "@/components/common/SidePanel";
import {
  Freshness,
  FreshnessBadge,
  SourceBadge,
  StationStatusBadge,
  fmtDir,
  fmtKts,
  fmtLastObs,
  freshnessOf,
} from "@/components/wind/stationDisplay";
import { canonicalIdOf, indexSnapshot, isPullCredential } from "@/components/wind/cloudflareStations";
import RejectedPushesView, {
  REJECTS_PARAM_PREFIX,
  fmtAgo,
  rejectMatchesStation,
  stationHintOf,
} from "@/components/wind/RejectedPushesView";

/**
 * Live-wind push-station credential admin (SRF-2747) — interim KV-backed surface
 * until the SRF-2331 registry. Credentials authenticate third-party station pushes
 * (Ecowitt / WU / Windguru / generic JSON) at the live-wind edge worker.
 */

type CreateFormState = {
  stationId: string;
  name: string;
  lat: string;
  lon: string;
  passkey: string;
  contactName: string;
  organization: string;
  organizationType: string;
  address: string;
  email: string;
  phone: string;
  website: string;
  stationType: string;
  hardware: string;
  mastHeightM: string;
  spotId: string;
  notes: string;
};

const emptyForm: CreateFormState = {
  stationId: "", name: "", lat: "", lon: "", passkey: "",
  contactName: "", organization: "", organizationType: "", address: "", email: "",
  phone: "", website: "", stationType: "", hardware: "", mastHeightM: "", spotId: "", notes: "",
};

type ContactFormFields = {
  contactName: string; organization: string; organizationType: string; address: string;
  email: string; phone: string; website: string; stationType: string; hardware: string;
  mastHeightM: string; spotId: string; notes: string;
};

const contactFromForm = (f: ContactFormFields): StationContact | undefined => {
  const contact: StationContact = {
    contactName: f.contactName.trim() || undefined,
    organization: f.organization.trim() || undefined,
    organizationType: f.organizationType || undefined,
    address: f.address.trim() || undefined,
    email: f.email.trim() || undefined,
    phone: f.phone.trim() || undefined,
    website: f.website.trim() || undefined,
    stationType: f.stationType || undefined,
    hardware: f.hardware.trim() || undefined,
    mastHeightM: f.mastHeightM.trim() ? Number(f.mastHeightM) : undefined,
    spotId: f.spotId.trim() ? Number(f.spotId) : undefined,
    notes: f.notes.trim() || undefined,
  };
  return Object.values(contact).some((v) => v !== undefined) ? contact : undefined;
};

/**
 * The path segment that addresses a station in the admin API.
 *
 * PUSH stations are addressed by their credential prefix. PULL stations have no credential
 * at all, so they are addressed by station id — without this the UI sent the literal
 * "undefined" and every action failed with "no credential with prefix undefined".
 */
const stationRef = (c: PushStationCredential): string => c.prefix ?? c.stationId;

/** Typed lat/lon strings -> a pin position, or null while they are not a valid point. */
const pickerCoords = (lat: string, lon: string): { lat: number; lon: number } | null => {
  if (!lat.trim() || !lon.trim()) return null;
  const la = Number(lat);
  const lo = Number(lon);
  if (!Number.isFinite(la) || !Number.isFinite(lo) || la < -90 || la > 90 || lo < -180 || lo > 180) {
    return null;
  }
  return { lat: la, lon: lo };
};

const selectCls =
  "w-full rounded bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:outline-none focus:border-accent";

const StationDetailFields: React.FC<{
  form: ContactFormFields;
  set: (field: keyof ContactFormFields, value: string) => void;
  inputCls: string;
}> = ({ form, set, inputCls }) => {
  const label = "mb-1 block text-sm text-white75";
  return (
    <>
      <p className="text-sm font-semibold text-white">Contact</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Contact person</label>
          <input className={inputCls} value={form.contactName}
            onChange={(e) => set("contactName", e.target.value)} />
        </div>
        <div>
          <label className={label}>Organization / school</label>
          <input className={inputCls} value={form.organization}
            onChange={(e) => set("organization", e.target.value)} />
        </div>
        <div>
          <label className={label}>Organization type</label>
          <select className={selectCls} value={form.organizationType}
            onChange={(e) => set("organizationType", e.target.value)}>
            <option value="">—</option>
            {ORGANIZATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Email</label>
          <input className={inputCls} type="email" value={form.email}
            onChange={(e) => set("email", e.target.value)} />
        </div>
        <div>
          <label className={label}>Phone</label>
          <input className={inputCls} value={form.phone}
            onChange={(e) => set("phone", e.target.value)} />
        </div>
        <div>
          <label className={label}>Website</label>
          <input className={inputCls} value={form.website}
            onChange={(e) => set("website", e.target.value)} />
        </div>
      </div>
      <div className="mt-3">
        <label className={label}>Address</label>
        <input className={inputCls} value={form.address}
          onChange={(e) => set("address", e.target.value)} />
      </div>
      <p className="mt-4 text-sm font-semibold text-white">Station details</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Placement type</label>
          <select className={selectCls} value={form.stationType}
            onChange={(e) => set("stationType", e.target.value)}>
            <option value="">—</option>
            {STATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Hardware</label>
          <input className={inputCls} placeholder="e.g. GW1100 + WS85" value={form.hardware}
            onChange={(e) => set("hardware", e.target.value)} />
        </div>
        <div>
          <label className={label}>Mast height (m)</label>
          <input className={inputCls} type="number" step="any" value={form.mastHeightM}
            onChange={(e) => set("mastHeightM", e.target.value)} />
        </div>
        <div>
          <label className={label}>Surfr spot id</label>
          <input className={inputCls} type="number" value={form.spotId}
            onChange={(e) => set("spotId", e.target.value)} />
        </div>
      </div>
      <div className="mt-3">
        <label className={label}>Notes</label>
        <textarea className={inputCls} rows={2} value={form.notes}
          onChange={(e) => set("notes", e.target.value)} />
      </div>
    </>
  );
};

type CreateModalProps = {
  onClose: () => void;
  onCreated: (token: string | undefined, mode: CredentialMode) => void;
  /** Pull mode is a wizard, not a branch of this form — the page swaps the dialog out. */
  onSwitchToPull: () => void;
};

/**
 * Push-credential registration (generate / Ecowitt PASSKEY). Pull stations do NOT go
 * through this form: picking "Pull" hands off to PullStationWizard, which asks only for
 * what the chosen format needs and can test the URL first (SRF-2870).
 */
const CreateStationModal: React.FC<CreateModalProps> = ({ onClose, onCreated, onSwitchToPull }) => {
  const [form, setForm] = useState<CreateFormState>({ ...emptyForm });
  const [mode, setMode] = useState<Exclude<CredentialMode, "pull">>("generate");
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const set = (field: keyof CreateFormState, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    setSaving(true);
    try {
      const req: CreatePushStationRequest = {
        stationId: form.stationId.trim(),
        name: form.name.trim(),
        lat: Number(form.lat),
        lon: Number(form.lon),
        credentialMode: mode,
        passkey: mode === "passkey" ? form.passkey.trim() : undefined,
        contact: contactFromForm(form),
      };
      const result = await PushStationService.create(req);
      if (result.contactSaved === false) {
        toast.warning("Credential created, but the registry row failed to save — open Settings and save once to retry");
      }
      onCreated(result.token, mode);
    } catch (err) {
      toast.error(getErrorMessage(err, "registering push credential"));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:outline-none focus:border-accent";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <form
        onSubmit={handleSubmit}
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-gray-800 p-6 shadow-xl space-y-4"
      >
        <h2 className="text-lg font-semibold text-white">Register station</h2>

        <div>
          <label className="mb-1 block text-sm text-white75">Station id (public, lowercase-dashes)</label>
          <input
            className={inputCls}
            value={form.stationId}
            onChange={(e) => set("stationId", e.target.value)}
            placeholder="kitepower-valkenburg"
            required
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-white75">Display name</label>
          <input
            className={inputCls}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Kitepower — Valkenburg"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm text-white75" htmlFor="create-lat">Latitude</label>
            <input
              id="create-lat"
              className={inputCls}
              type="number"
              step="any"
              value={form.lat}
              onChange={(e) => set("lat", e.target.value)}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-white75" htmlFor="create-lon">Longitude</label>
            <input
              id="create-lon"
              className={inputCls}
              type="number"
              step="any"
              value={form.lon}
              onChange={(e) => set("lon", e.target.value)}
              required
            />
          </div>
        </div>
        {/* Click / drag on the map fills the two fields above; typing moves the pin. */}
        <LocationPickerMap
          lat={pickerCoords(form.lat, form.lon)?.lat ?? null}
          lon={pickerCoords(form.lat, form.lon)?.lon ?? null}
          onPick={(la, lo) => setForm((f) => ({ ...f, lat: String(la), lon: String(lo) }))}
          heightPx={240}
        />

        <div>
          <label className="mb-1 block text-sm text-white75">How does the data arrive?</label>
          <div className="flex flex-wrap gap-4 text-sm text-white">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={mode === "generate"}
                onChange={() => setMode("generate")}
              />
              Push — generate token (WU / weewx / generic)
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={mode === "passkey"}
                onChange={() => setMode("passkey")}
              />
              Push — Ecowitt PASSKEY
            </label>
            <label className="flex items-center gap-1">
              <input type="radio" checked={false} onChange={onSwitchToPull} />
              Pull — we fetch it (guided setup)
            </label>
          </div>
          <p className="mt-1 text-xs text-white75">
            Pull opens a short wizard: pick the source format, test the URL, then fill in
            the station. A pull station gets NO push credential.
          </p>
        </div>
        {mode === "passkey" && (
          <div>
            <label className="mb-1 block text-sm text-white75">
              Device PASSKEY (from the WS View app — never displayed again)
            </label>
            <input
              className={inputCls}
              value={form.passkey}
              onChange={(e) => set("passkey", e.target.value)}
              minLength={10}
              required
            />
          </div>
        )}

        <div className="border-t border-gray-700 pt-3">
          <p className="mb-2 text-sm font-medium text-white">Contact & station details (ops metadata — stored in our DB, never public)</p>
          <StationDetailFields form={form} set={set} inputCls={inputCls} />
        </div>

        <p className="text-xs text-white75">
          The station becomes publicly visible on the live wind map after its first accepted
          push — registration is the approval.
        </p>
        {fieldError && <p className="text-sm text-red-300">{fieldError}</p>}

        <div className="sticky bottom-0 -mx-6 -mb-6 mt-2 flex justify-end gap-2 border-t border-gray-700 bg-gray-800 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-4 py-2 text-white75 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded bg-accent px-4 py-2 font-medium text-white disabled:opacity-50"
          >
            {saving ? "Registering…" : "Register"}
          </button>
        </div>
      </form>
    </div>
  );
};

/** Edit ops contact metadata for an existing station (SRF-2748). */
/** Consent summary + the admin's record-on-behalf-of action. */
const ConsentSection: React.FC<{
  station: PushStationCredential;
  onRecorded: () => void;
}> = ({ station, onRecorded }) => {
  const c = station.contact ?? {};
  const [evidence, setEvidence] = useState("");
  const [saving, setSaving] = useState(false);
  const hasConsent = Boolean(c.dataConsentVersion && c.dataConsentAt);
  const byAdmin = (c.dataConsentActor ?? "").startsWith("admin:");
  const inputCls =
    "w-full rounded bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:outline-none focus:border-accent";

  const record = async () => {
    if (!evidence.trim()) {
      toast.error("Describe how the owner's agreement was obtained");
      return;
    }
    setSaving(true);
    try {
      await PushStationService.recordConsent(stationRef(station), evidence.trim());
      toast.success("Consent recorded");
      setEvidence("");
      onRecorded();
    } catch (err) {
      toast.error(getErrorMessage(err, "recording consent"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <p className="mt-4 text-sm font-semibold text-white">Data-usage consent</p>
      {hasConsent ? (
        <div className="rounded border border-gray-600 bg-gray-700/40 p-3 text-sm">
          <p className="text-white">
            {byAdmin ? "Recorded by an admin" : "Accepted by the owner"} ·{" "}
            <span className="text-white75">{c.dataConsentVersion}</span>
          </p>
          <p className="mt-1 text-xs text-white75">
            {c.dataConsentAt}
            {c.dataConsentIp ? ` · from ${c.dataConsentIp}` : ""}
            {c.dataConsentActor ? ` · ${c.dataConsentActor}` : ""}
          </p>
          {byAdmin && (
            // An admin asserting "the owner agreed" is weaker evidence than the owner
            // clicking accept. Say so plainly rather than showing both as a green tick.
            <p className="mt-2 text-xs text-amber-300">
              Recorded on the owner&apos;s behalf — the evidence is in the notes.
            </p>
          )}
        </div>
      ) : (
        <div className="rounded border border-amber-500/40 bg-amber-900/20 p-3">
          <p className="text-sm text-amber-200">
            No consent on record. This station pre-dates consent capture, or was created
            before the owner accepted the policy.
          </p>
          <label className="mb-1 mt-3 block text-sm text-white75">
            How was the owner&apos;s agreement obtained?
          </label>
          <textarea
            className={inputCls}
            rows={2}
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            placeholder="e.g. email from owner@kiteschool.com on 2026-08-01 agreeing to publish live wind"
          />
          <button
            type="button"
            onClick={() => void record()}
            disabled={saving || !evidence.trim()}
            className="mt-2 rounded bg-accent px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? "Recording…" : "Record consent on their behalf"}
          </button>
        </div>
      )}
    </>
  );
};

/**
 * ONE settings view for everything about a station — the same shape as the create dialog,
 * rather than the three separate modals (station / contact / credential) this replaces.
 * Those hit different endpoints and different stores, which is an implementation detail
 * admins should not have to hold in their head: name and placement type living in
 * different dialogs is why the page felt "split very strangely".
 *
 * Underneath it is still two writes (KV credential + Postgres metadata). They are ordered
 * and reported honestly: if the second fails, the first is NOT silently presented as a
 * complete save.
 */
const StationSettingsModal: React.FC<{
  station: PushStationCredential;
  onClose: () => void;
  onSaved: () => void;
  /** Pull config is edited in the wizard (SRF-2870), not inline here. */
  onEditPull: () => void;
}> = ({ station, onClose, onSaved, onEditPull }) => {
  const c = station.contact ?? {};
  const [name, setName] = useState(station.name ?? "");
  const [lat, setLat] = useState(station.lat != null ? String(station.lat) : "");
  const [lon, setLon] = useState(station.lon != null ? String(station.lon) : "");
  const [ratePerMin, setRatePerMin] = useState(
    station.ratePerMin != null ? String(station.ratePerMin) : ""
  );
  const [form, setForm] = useState<ContactFormFields>({
    contactName: c.contactName ?? "",
    organization: c.organization ?? "",
    organizationType: c.organizationType ?? "",
    address: c.address ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    website: c.website ?? "",
    stationType: c.stationType ?? "",
    hardware: c.hardware ?? "",
    mastHeightM: c.mastHeightM != null ? String(c.mastHeightM) : "",
    spotId: c.spotId != null ? String(c.spotId) : "",
    notes: c.notes ?? "",
  });
  const setField = (field: keyof ContactFormFields, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [clearingPull, setClearingPull] = useState(false);

  /**
   * Revert a pull station to push-only. The wizard can only WRITE a pull config — it has
   * no "none" format — so removing one has to live here, next to the summary it removes.
   * `PUT /{ref}/pull` with an absent pollUrl is the backend's clearing form (see
   * UpdatePullRequest); the fields are sent explicitly undefined exactly as the inline
   * pull editor did before the wizard replaced it.
   */
  const handleClearPull = async () => {
    if (
      !window.confirm(
        `Remove the pull config from ${station.stationId}?\n\n` +
          "We stop fetching it and the station becomes push-only; the stored upstream " +
          "token is dropped. Its id, history and any push credential are kept."
      )
    ) {
      return;
    }
    setFieldError(null);
    setClearingPull(true);
    try {
      await PushStationService.updatePull(stationRef(station), {
        pollUrl: undefined,
        pollProvider: undefined,
        pollIntervalS: undefined,
        pollExternalId: undefined,
        pollAuthToken: undefined,
      });
      toast.success(`Pull config removed from ${station.stationId}`);
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, "removing pull config"));
    } finally {
      setClearingPull(false);
    }
  };

  const inputCls =
    "w-full rounded bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:outline-none focus:border-accent";
  const label = "mb-1 block text-sm text-white75";

  const latNum = lat.trim() === "" ? null : Number(lat);
  const lonNum = lon.trim() === "" ? null : Number(lon);
  const coordsValid =
    latNum != null &&
    lonNum != null &&
    Number.isFinite(latNum) &&
    Number.isFinite(lonNum) &&
    latNum >= -90 &&
    latNum <= 90 &&
    lonNum >= -180 &&
    lonNum <= 180;

  /** Map click / search result -> the number inputs (two-way with the fields below). */
  const handlePick = (pickedLat: number, pickedLon: number) => {
    setLat(String(pickedLat));
    setLon(String(pickedLon));
    setFieldError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setFieldError("Display name is required.");
      return;
    }
    if (!coordsValid) {
      setFieldError("Latitude must be between -90 and 90, longitude between -180 and 180.");
      return;
    }
    // The backend binds ratePerMin as an Integer: a NaN would serialize to null and
    // silently CLEAR the override, and a decimal would 400. Reject both here instead.
    const rateRaw = ratePerMin.trim();
    const rateNum = rateRaw === "" ? undefined : Number(rateRaw);
    if (rateNum !== undefined && (!Number.isInteger(rateNum) || rateNum < 1)) {
      setFieldError("Rate limit must be a whole number of pushes per minute, or empty for the default.");
      return;
    }
    setFieldError(null);
    setSaving(true);

    // Two writes across two stores (KV credential, then Postgres metadata). Sequential and
    // reported per step: a partial save must never surface as a clean "Saved", or an admin
    // walks away believing an edit landed that did not. Pull config is NOT written here —
    // it has its own wizard and its own endpoint.
    try {
      await PushStationService.updateStation(stationRef(station), {
        name: name.trim(),
        lat: latNum as number,
        lon: lonNum as number,
        ratePerMin: rateNum,
      });
    } catch (err) {
      toast.error(getErrorMessage(err, "saving station"));
      setSaving(false);
      return;
    }

    try {
      await PushStationService.updateContact(stationRef(station), contactFromForm(form) ?? {});
    } catch (err) {
      toast.error(getErrorMessage(err, "saving contact/details"));
      toast.warning("Name and position were saved; the details were not.");
      setSaving(false);
      onSaved();
      return;
    }

    toast.success(`Saved ${station.stationId}`);
    setSaving(false);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <form
        onSubmit={handleSubmit}
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-gray-800 p-6 shadow-xl space-y-4"
      >
        <h2 className="text-lg font-semibold text-white">
          Station settings — {station.stationId}
        </h2>

        <div>
          <label className={label}>Station id (immutable)</label>
          <input
            className={`${inputCls} cursor-not-allowed opacity-60`}
            value={station.stationId}
            readOnly
            disabled
          />
          <p className="mt-1 text-xs text-white75">
            The station id keys the public map identity and every stored observation —
            changing it would orphan the station&apos;s history. Register a new station instead.
          </p>
        </div>

        <div>
          <label className={label}>Display name</label>
          <input
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <p className="mt-1 text-xs text-white75">
            Shown on the live wind map; picked up on the next publish cycle.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label} htmlFor="settings-lat">Latitude</label>
            <input
              id="settings-lat"
              className={inputCls}
              type="number"
              step="any"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              required
            />
          </div>
          <div>
            <label className={label} htmlFor="settings-lon">Longitude</label>
            <input
              id="settings-lon"
              className={inputCls}
              type="number"
              step="any"
              value={lon}
              onChange={(e) => setLon(e.target.value)}
              required
            />
          </div>
        </div>

        <LocationPickerMap
          lat={coordsValid ? (latNum as number) : null}
          lon={coordsValid ? (lonNum as number) : null}
          onPick={handlePick}
          heightPx={260}
        />

        <div>
          <label className={label}>Rate limit (pushes/min)</label>
          <input
            className={inputCls}
            type="number"
            min={1}
            value={ratePerMin}
            onChange={(e) => setRatePerMin(e.target.value)}
            placeholder="leave empty for the default (60)"
          />
        </div>

        {/* Everything the create dialog shows, in the same order — contact block first,
            then station details (placement type, hardware, mast, spot), then notes. */}
        <StationDetailFields form={form} set={setField} inputCls={inputCls} />

        {/* Pull ingest is edited in its own wizard (SRF-2870): the fields a format needs
            differ per format, the interval bounds come from the backend catalogue, and the
            URL can be tested before saving — none of which fits a flat section here. This
            block only SHOWS the current config; the button opens the wizard. */}
        <div className="border-t border-gray-700 pt-3">
          <p className="text-sm font-semibold text-white">Pull ingest (we fetch this station)</p>
          {c.pollUrl ? (
            <div className="mt-2 rounded border border-gray-600 bg-gray-700/40 p-3 text-sm">
              <p className="break-all text-white">{c.pollUrl}</p>
              <p className="mt-1 text-xs text-white75">
                {c.pollProvider ?? "?"} · every {c.pollIntervalS ?? "?"}s
                {c.pollExternalId ? ` · id ${c.pollExternalId}` : ""}
                {c.pollAuthTokenConfigured ? " · token configured" : ""}
              </p>
            </div>
          ) : (
            <p className="mt-2 text-sm text-white75">
              Not a pull station — data arrives by push on its credential.
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onEditPull}
              className="rounded bg-gray-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-600"
            >
              {c.pollUrl ? "Edit pull config…" : "Configure pull ingest…"}
            </button>
            {c.pollUrl && (
              <button
                type="button"
                onClick={() => void handleClearPull()}
                disabled={clearingPull}
                className="rounded border border-red-700 bg-red-900/30 px-3 py-1.5 text-sm font-medium text-red-200 hover:bg-red-900/50 disabled:opacity-50"
              >
                {clearingPull ? "Removing…" : "Remove pull config (station becomes push-only)"}
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-white75">
            Opens the pull wizard. Unsaved edits in this dialog are not carried over.
          </p>
        </div>

        <ConsentSection station={station} onRecorded={onSaved} />

        {fieldError && <div className="text-sm text-red-400">{fieldError}</div>}

        <p className="text-xs text-white75">
          The push credential and the approval status are not affected by this edit — use
          the Credential action to rotate the token.
        </p>

        <div className="sticky bottom-0 -mx-6 -mb-6 mt-2 flex justify-end gap-2 border-t border-gray-700 bg-gray-800 px-6 py-4">
          <button type="button" onClick={onClose} className="rounded px-4 py-2 text-white75 hover:text-white">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded bg-accent px-4 py-2 font-medium text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

/**
 * Rotation grace (SRF-2867): an ACTIVE station's old token keeps working this long after a
 * replacement. Must match the backend's LiveWindWorkerNotifier.ROTATION_GRACE_MS (enforced).
 */
export const ROTATION_GRACE_MINUTES = 5;

const clockTime = (ms: number): string =>
  new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/**
 * Confirmation text for a credential replacement. When a rotation already happened inside the
 * grace window (`graceUntil` in the future), it spells out that the token from that change is
 * superseded: the Kitepower El Gouna outage (2026-10-20) was a second rotation 31 s after the
 * first, with neither new token ever reaching the device.
 */
export const credentialConfirmMessage = (
  station: Pick<PushStationCredential, "stationId" | "status">,
  graceUntil: number | null | undefined,
  now: number = Date.now()
): string => {
  const lines = [`Replace the credential for "${station.stationId}"?`];
  if (station.status === "active") {
    lines.push(
      `The current token or PASSKEY keeps working for ${ROTATION_GRACE_MINUTES} minutes, then only the new one is accepted. Put the new credential into the device right away.`
    );
  } else {
    lines.push(
      "The current token or PASSKEY stops working immediately and the device must be reconfigured before it can push again."
    );
  }
  if (graceUntil != null && graceUntil > now) {
    lines.push(
      `WARNING: this credential was already changed less than ${ROTATION_GRACE_MINUTES} minutes ago. ` +
        "Changing it again SUPERSEDES the token from that change, so that token also stops working within " +
        `${ROTATION_GRACE_MINUTES} minutes. Only continue if it was never put on the device; otherwise ` +
        "cancel and configure the device with that token instead."
    );
  }
  return lines.join("\n\n");
};

/**
 * Replace a station's push credential. Destructive for the device: the old token stops
 * working after the grace window (or at once for a pending station), so the warning is
 * front and centre.
 */
const CredentialModal: React.FC<{
  station: PushStationCredential;
  /** Latest known rotation-grace end for this station (list data or this session's rotation). */
  graceUntil?: number | null;
  onClose: () => void;
  onReplaced: (token: string | undefined, graceUntil: number | null) => void;
}> = ({ station, graceUntil, onClose, onReplaced }) => {
  const [mode, setMode] = useState<"generate" | "passkey">("generate");
  const [passkey, setPasskey] = useState("");
  const [saving, setSaving] = useState(false);

  const inputCls =
    "w-full rounded bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:outline-none focus:border-accent";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // A blank/whitespace PASSKEY would fall through to the generate path and mint a token
    // the admin never asked for — make the intent explicit instead.
    if (mode === "passkey" && !passkey.trim()) {
      toast.error("Enter the device PASSKEY, or switch to “Generate token”.");
      return;
    }
    if (!window.confirm(credentialConfirmMessage(station, graceUntil))) {
      return;
    }
    setSaving(true);
    try {
      // Push-only: the Credential button is hidden for pull stations, which have none.
      const result = await PushStationService.replaceCredential(
        station.prefix as string,
        mode === "passkey" ? passkey.trim() : undefined
      );
      const until = typeof result.graceUntil === "number" ? result.graceUntil : null;
      toast.success(
        until
          ? `Credential replaced for ${station.stationId}. The old one keeps working until ${clockTime(until)}: update the device now.`
          : `Credential replaced for ${station.stationId}`
      );
      onReplaced(result.token, until);
    } catch (err) {
      toast.error(getErrorMessage(err, "replacing credential"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <form
        onSubmit={handleSubmit}
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-gray-800 p-6 shadow-xl space-y-4"
      >
        <h2 className="text-lg font-semibold text-white">
          Change credential — {station.stationId}
        </h2>

        <div className="rounded border border-amber-700 bg-amber-900/30 p-3 text-sm text-amber-200">
          {station.status === "active"
            ? `The station's current credential keeps working for ${ROTATION_GRACE_MINUTES} minutes after this is saved, then only the new one is accepted.`
            : "The station's current credential stops working as soon as this is saved."}{" "}
          The device has to be reconfigured with the new token or PASSKEY right away. The
          station keeps its current status ({station.status}).
        </div>

        {graceUntil != null && graceUntil > Date.now() && (
          <div role="alert" className="rounded border border-red-700 bg-red-900/30 p-3 text-sm text-red-200">
            This credential was already changed less than {ROTATION_GRACE_MINUTES} minutes ago.
            Changing it again supersedes the token from that change. If that token is already
            on the device, cancel and keep it.
          </div>
        )}

        <div>
          <label className="mb-1 block text-sm text-white75">New credential</label>
          <div className="flex gap-4 text-sm text-white">
            <label className="flex items-center gap-1">
              <input type="radio" checked={mode === "generate"} onChange={() => setMode("generate")} />
              Generate token (WU / weewx / generic)
            </label>
            <label className="flex items-center gap-1">
              <input type="radio" checked={mode === "passkey"} onChange={() => setMode("passkey")} />
              Ecowitt PASSKEY
            </label>
          </div>
        </div>

        {mode === "passkey" && (
          <div>
            <label className="mb-1 block text-sm text-white75">
              Device PASSKEY (from the WS View app — never displayed again)
            </label>
            <input
              className={inputCls}
              value={passkey}
              onChange={(e) => setPasskey(e.target.value)}
              minLength={10}
              required
            />
          </div>
        )}

        <div className="sticky bottom-0 -mx-6 -mb-6 mt-2 flex justify-end gap-2 border-t border-gray-700 bg-gray-800 px-6 py-4">
          <button type="button" onClick={onClose} className="rounded px-4 py-2 text-white75 hover:text-white">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded bg-accent px-4 py-2 font-medium text-white disabled:opacity-50"
          >
            {saving ? "Replacing…" : "Replace credential"}
          </button>
        </div>
      </form>
    </div>
  );
};

/** Copy-once display for a generated token — it is not retrievable after this dialog. */
const TokenOnceModal: React.FC<{ token: string; graceUntil?: number | null; onClose: () => void }> = ({
  token,
  graceUntil,
  onClose,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
    <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-gray-800 p-6 shadow-xl space-y-4">
      <h2 className="text-lg font-semibold text-white">Push token — shown once</h2>
      <p className="text-sm text-white75">
        Give this token to the station owner (WU password / Bearer token). It is{" "}
        <span className="font-semibold text-white">not stored</span> and cannot be shown
        again — losing it means revoking and re-registering.
      </p>
      {graceUntil != null && (
        <div role="alert" className="rounded border border-amber-700 bg-amber-900/30 p-3 text-sm text-amber-200">
          The device keeps working with the OLD token for {ROTATION_GRACE_MINUTES} minutes (until{" "}
          {clockTime(graceUntil)}). Put this new token into the device now; after that only the
          new token is accepted. Do not change the credential again before the device uses it.
        </div>
      )}
      <div className="flex items-center gap-2 rounded bg-gray-900 p-3 font-mono text-sm text-white break-all">
        <span className="flex-1">{token}</span>
        <button
          onClick={() =>
            navigator.clipboard
              .writeText(token)
              .then(() => toast.success("Token copied"))
              .catch(() => toast.error("Copy failed — select and copy the token manually"))
          }
          className="rounded bg-accent px-3 py-1 text-xs font-medium text-white"
        >
          Copy
        </button>
      </div>
      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="rounded bg-gray-700 px-4 py-2 text-white hover:bg-gray-600"
        >
          I stored it — close
        </button>
      </div>
    </div>
  </div>
);

// --- station-management table ----------------------------------------------------------

/**
 * Default sort: STATUS (pending → active → revoked), then station id — anything awaiting a
 * decision floats to the top even in the "All stations" view. Page size 25 by default:
 * the registry is hundreds of rows, not thousands.
 */
const MANAGE_PAGE_SIZES = [25, 50, 100];
const MANAGE_PAGE_SIZE = 25;
const MANAGE_FILTERS = {
  ingest: "all",
  status: "all",
  consent: "all",
  provider: "all",
  type: "all",
  fresh: "all",
};
type ManageFilters = typeof MANAGE_FILTERS;

interface ManageRow {
  c: PushStationCredential;
  isPull: boolean;
  /** The station's Cloudflare snapshot entry (current live values), joined on canonical id. */
  live?: SnapshotStation;
  freshness: Freshness;
  consent: "owner" | "admin" | "none";
  /** pull provider (tempest / clientraw / json …) or "push". */
  provider: string;
}

const consentOf = (c: PushStationCredential): ManageRow["consent"] =>
  !c.contact?.dataConsentVersion
    ? "none"
    : (c.contact.dataConsentActor ?? "").startsWith("admin:")
      ? "admin"
      : "owner";

const STATUS_RANK: Record<string, number> = { pending: 0, active: 1, revoked: 2 };
const CONSENT_RANK = { none: 0, admin: 1, owner: 2 } as const;
const FRESH_RANK = { live: 0, stale: 1, nodata: 2 } as const;

const MANAGE_ACCESSORS: SortAccessors<ManageRow> = {
  id: (r) => r.c.stationId,
  name: (r) => r.c.name || r.c.stationId,
  ingest: (r) => r.provider,
  status: (r) => STATUS_RANK[r.c.status] ?? 9,
  consent: (r) => CONSENT_RANK[r.consent],
  wind: (r) => r.live?.windKts,
  obs: (r) => r.live?.obsTs,
  fresh: (r) => FRESH_RANK[r.freshness],
};

const PushStationsPage: React.FC = () => {
  const [stations, setStations] = useState<PushStationCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // Pull registration and pull editing are the SAME wizard; null station = create.
  const [pullWizard, setPullWizard] = useState<{ station: PushStationCredential | null } | null>(null);
  const [tokenOnce, setTokenOnce] = useState<string | null>(null);
  const [tokenOnceGraceUntil, setTokenOnceGraceUntil] = useState<number | null>(null);
  /** Rotation-grace end per station from replacements made in this session (the list may lag). */
  const [rotatedUntil, setRotatedUntil] = useState<Record<string, number>>({});
  // One target for the whole settings dialog — the separate contact/station targets are
  // gone along with the separate modals.
  const [editTarget, setEditTarget] = useState<PushStationCredential | null>(null);
  const [credentialTarget, setCredentialTarget] = useState<PushStationCredential | null>(null);
  /** "Test now" on pending pull requests, keyed by station id. */
  const [pullTests, setPullTests] = useState<
    Record<string, { running: boolean; result?: PullTestResult }>
  >({});

  /** Cloudflare snapshot, for each station's CURRENT live values (joined on canonical id). */
  const [snapshot, setSnapshot] = useState<SnapshotStation[]>([]);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const table = useTableState<ManageFilters>({
    defaultFilters: MANAGE_FILTERS,
    defaultSort: { key: "status", dir: "asc" },
    defaultPageSize: MANAGE_PAGE_SIZE,
    pageSizes: MANAGE_PAGE_SIZES,
    descFirst: ["wind", "obs"],
  });
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // The registry is required; live values are an enrichment — a failed snapshot must not
    // blank the management list.
    const [regs, snap] = await Promise.allSettled([
      PushStationService.fetchAll(),
      PushStationService.fetchAllCloudflare(),
    ]);
    if (regs.status === "fulfilled") setStations(regs.value);
    else setError(getErrorMessage(regs.reason, "loading push stations"));
    if (snap.status === "fulfilled") {
      setSnapshot(snap.value?.stations ?? []);
      setLiveError(null);
    } else {
      setSnapshot([]);
      setLiveError(getErrorMessage(snap.reason, "loading live values"));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreated = (token: string | undefined, _mode: CredentialMode) => {
    setCreateOpen(false);
    if (token) {
      setTokenOnce(token);
    }
    toast.success("Credential registered and verified");
    load();
  };

  const handlePullSaved = (mode: "create" | "edit") => {
    setPullWizard(null);
    toast.success(
      mode === "create"
        ? "Pull station registered — fetching starts on the next ingest run"
        : "Pull config saved — the next ingest run uses it"
    );
    load();
  };

  const handleDeactivateClick = async (c: PushStationCredential) => {
    if (!window.confirm(`Deactivate station "${c.stationId}"?\n\nPushes will be rejected within ~60 s. Nothing is deleted — you can activate it again anytime.`)) {
      return;
    }
    try {
      await PushStationService.revoke(stationRef(c));
      toast.success(`Deactivated ${c.stationId} — pushes rejected within ~60s (reversible)`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "deactivating push station"));
    }
  };

  /**
   * Permanently delete a credential (SRF-2803).
   *
   * Deliberately harder to trigger than Deactivate. The other destructive actions on this
   * page are reversible, so a window.confirm is proportionate for them; this one is not
   * reversible, so it asks the operator to TYPE the station id. A confirm dialog is one
   * stray Enter away from deleting the wrong row, and the row is identified by a 10-char
   * opaque prefix nobody can eyeball. The backend enforces the same check independently —
   * this is the humane half of the guard, not the security half.
   */
  const handleDeleteClick = async (c: PushStationCredential) => {
    // Pull and push stations are deleted differently — a pull station has no KV credential,
    // so "delete" retires its Postgres pull config and drops the owner's upstream token.
    // Say which one is about to happen; the consequences are not the same.
    const isPull = !c.prefix;
    const isActive = c.status === "active";
    const what = isPull
      ? `This cannot be undone. Fetching stops within ~1 min, the station leaves the map on ` +
        `the next publish, and the station record (contact, consent, the owner's upstream ` +
        `API token) is removed from the database.`
      : `This cannot be undone. The credential is removed from Cloudflare KV (the device ` +
        `token stops working within ~60 s) and the station record is removed from the database.`;
    const warning = !isActive
      ? ""
      : isPull
        ? `\n\n⚠️  This station is ACTIVE and currently being fetched.`
        : `\n\n⚠️  This credential is ACTIVE. If a device is still pushing to it, it will start failing immediately.`;
    const typed = window.prompt(
      `PERMANENTLY DELETE "${c.stationId}"?\n\n` +
        `${what}\n\n` +
        `Kept: past observations only. The station id and name become free to register again.\n` +
        `To stop a station reversibly, use Deactivate instead.${warning}\n\n` +
        `Type the station id to confirm:`,
    );
    if (typed === null) return; // cancelled
    if (typed.trim() !== c.stationId) {
      toast.error("Station id did not match — nothing was deleted");
      return;
    }
    try {
      const result = await PushStationService.delete(stationRef(c), c.stationId);
      if (result.contactDeleted) {
        toast.success(`Deleted ${result.stationId} — id and name are free to register again`);
      } else {
        toast.error(
          `${result.stationId}: credential removed from Cloudflare, but the database row could not be removed — the station id is still taken`,
        );
      }
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "deleting push station"));
    }
  };

  const handleApprove = async (c: PushStationCredential) => {
    try {
      await PushStationService.approve(stationRef(c));
      toast.success(`Approved ${c.stationId} — pushes accepted and station publicly visible`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "approving push station"));
    }
  };

  const handleRejectClick = async (c: PushStationCredential) => {
    if (!window.confirm(`Reject station request "${c.stationId}"?\n\nThe credential is revoked — the owner has to submit a new request.`)) {
      return;
    }
    const reason = window.prompt("Optional reject reason (visible to the owner):") ?? undefined;
    try {
      await PushStationService.reject(stationRef(c), reason?.trim() || undefined);
      toast.success(`Rejected ${c.stationId}`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "rejecting push station"));
    }
  };

  /**
   * One server-side test of a pending self-service pull request with its stored URL and
   * mapping — the reviewer sees whether it actually parses before approving. The stored
   * token (if any) is used via stationRef; nothing is persisted.
   */
  const handleTestNow = async (c: PushStationCredential) => {
    const contact = c.contact;
    if (!contact?.pollUrl || !contact.pollProvider) return;
    setPullTests((t) => ({ ...t, [c.stationId]: { running: true } }));
    try {
      const result = await PushStationService.testPull({
        provider: contact.pollProvider,
        url: contact.pollUrl,
        externalId: contact.pollExternalId ?? null,
        ...(contact.pollOptions ? { options: contact.pollOptions } : {}),
        stationRef: stationRef(c),
      });
      setPullTests((t) => ({ ...t, [c.stationId]: { running: false, result } }));
    } catch (err) {
      setPullTests((t) => ({
        ...t,
        [c.stationId]: { running: false, result: { ok: false, error: getErrorMessage(err, "testing pull") } },
      }));
    }
  };

  const handleReactivate = async (c: PushStationCredential) => {
    try {
      await PushStationService.reactivate(stationRef(c));
      toast.success(`Activated ${c.stationId}`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "reactivating push credential"));
    }
  };


  // --- derived table data ------------------------------------------------------------
  const rows = useMemo<ManageRow[]>(() => {
    const byId = indexSnapshot(snapshot);
    return stations.map((c) => {
      const live = byId.get(canonicalIdOf(c));
      return {
        c,
        isPull: isPullCredential(c),
        live,
        freshness: freshnessOf(live?.stale, !live),
        consent: consentOf(c),
        provider: isPullCredential(c) ? c.contact?.pollProvider || "pull" : "push",
      };
    });
  }, [stations, snapshot]);

  const pendingCount = useMemo(() => rows.filter((r) => r.c.status === "pending").length, [rows]);
  // "Needs review" is the default whenever something is waiting; an explicit ?view= wins.
  const explicitView = params.get("view");
  const view: "review" | "all" | "rejects" =
    explicitView === "review" || explicitView === "all" || explicitView === "rejects"
      ? explicitView
      : pendingCount > 0
        ? "review"
        : "all";
  /** `rejectsQuery` pre-fills the rejected-pushes search (the side panel's deep link). */
  const setView = (v: "review" | "all" | "rejects", rejectsQuery?: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("view", v);
        next.delete("page");
        if (rejectsQuery !== undefined) {
          next.set(`${REJECTS_PARAM_PREFIX}q`, rejectsQuery);
          next.delete(`${REJECTS_PARAM_PREFIX}page`);
        }
        return next;
      },
      { replace: true }
    );

  const providers = useMemo(() => Array.from(new Set(rows.map((r) => r.provider))).sort(), [rows]);
  const stationTypes = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => r.c.contact?.stationType).filter((t): t is string => Boolean(t)))
      ).sort(),
    [rows]
  );

  const { filters, setFilter, query, sort } = table;
  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (view === "review" && r.c.status !== "pending") return false;
        if (filters.ingest !== "all" && (r.isPull ? "pull" : "push") !== filters.ingest) return false;
        if (filters.status !== "all" && r.c.status !== filters.status) return false;
        if (filters.consent !== "all" && r.consent !== filters.consent) return false;
        if (filters.provider !== "all" && r.provider !== filters.provider) return false;
        if (filters.type !== "all" && r.c.contact?.stationType !== filters.type) return false;
        if (filters.fresh !== "all" && r.freshness !== filters.fresh) return false;
        if (!query) return true;
        const ct = r.c.contact;
        return [
          r.c.stationId,
          r.c.name,
          r.c.canonicalStationId,
          r.provider,
          ct?.contactName,
          ct?.organization,
          ct?.email,
        ].some((v) => (v || "").toLowerCase().includes(query));
      }),
    [rows, view, filters, query]
  );
  const sorted = useMemo(
    () => sortRows(filtered, sort, MANAGE_ACCESSORS, (a, b) => a.c.stationId.localeCompare(b.c.stationId)),
    [filtered, sort]
  );
  const { pageRows, page, totalPages, total } = paginate(sorted, table.page, table.pageSize);
  const selected = selectedId ? rows.find((r) => r.c.stationId === selectedId) ?? null : null;

  const actionsFor = (c: PushStationCredential): ActionItem[] => {
    const items: ActionItem[] = [{ label: "Settings", onSelect: () => setEditTarget(c) }];
    if (c.status !== "revoked" && c.prefix) {
      items.push({ label: "Credential", onSelect: () => setCredentialTarget(c) });
    }
    if (c.status === "pending" && c.contact?.pollUrl) {
      items.push({
        label:
          c.contact.pollProvider === "json" || c.contact.pollOptions ? "Edit mapping" : "Edit pull config",
        onSelect: () => setPullWizard({ station: c }),
      });
      items.push({
        label: "Test now",
        onSelect: () => void handleTestNow(c),
        disabled: pullTests[c.stationId]?.running,
      });
    }
    if (c.status === "pending") {
      items.push({ label: "Approve", tone: "success", dividerBefore: true, onSelect: () => handleApprove(c) });
      items.push({ label: "Reject", tone: "danger", onSelect: () => handleRejectClick(c) });
    } else if (c.status === "active") {
      items.push({ label: "Deactivate", tone: "danger", dividerBefore: true, onSelect: () => handleDeactivateClick(c) });
    } else {
      items.push({ label: "Activate", tone: "success", dividerBefore: true, onSelect: () => handleReactivate(c) });
    }
    // Delete is available in every status; the typed-id confirmation is what guards it.
    items.push({
      label: "Delete",
      tone: "muted",
      dividerBefore: true,
      title: c.prefix
        ? "Permanently delete this credential (cannot be undone)"
        : "Permanently retire this pull station and delete the owner's token",
      onSelect: () => handleDeleteClick(c),
    });
    return items;
  };

  const colSpan = 7;
  const btn = "rounded-md px-3 py-2 text-sm font-medium text-white";

  return (
    <div className="min-h-full bg-gray-900 py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/wind-stations" className="text-sm text-cyan-400 hover:text-cyan-300">
            ← Live stations
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-white">Manage stations</h1>
          <p className="mt-1 text-sm text-gray-400">
            Registered push and pull stations — credentials, approvals, contact and consent.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className={`${btn} inline-flex items-center gap-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50`}
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setPullWizard({ station: null })}
            className={`${btn} bg-gray-700 hover:bg-gray-600`}
          >
            Register pull station
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className={`${btn} bg-blue-600 hover:bg-blue-500`}
          >
            Register push station
          </button>
        </div>
      </div>

      {/* Review queue first: pending requests are the thing that needs a human. */}
      <div className="mb-4 flex flex-wrap items-center gap-2" role="group" aria-label="Station list">
        <button
          type="button"
          aria-pressed={view === "review"}
          onClick={() => setView("review")}
          className={`rounded-md border px-3 py-1.5 text-sm font-medium ${
            view === "review"
              ? "border-yellow-500 bg-yellow-500/15 text-yellow-200"
              : pendingCount > 0
                ? "border-yellow-700/60 bg-gray-800 text-yellow-300 hover:bg-gray-700"
                : "border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700"
          }`}
        >
          Needs review ({pendingCount})
        </button>
        <button
          type="button"
          aria-pressed={view === "all"}
          onClick={() => setView("all")}
          className={`rounded-md border px-3 py-1.5 text-sm font-medium ${
            view === "all"
              ? "border-cyan-500 bg-cyan-500/15 text-cyan-200"
              : "border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700"
          }`}
        >
          All stations ({rows.length})
        </button>
        <button
          type="button"
          aria-pressed={view === "rejects"}
          onClick={() => setView("rejects")}
          className={`rounded-md border px-3 py-1.5 text-sm font-medium ${
            view === "rejects"
              ? "border-red-500 bg-red-500/15 text-red-200"
              : "border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700"
          }`}
        >
          Rejected pushes
        </button>
      </div>

      {view === "rejects" ? (
        <RejectedPushesView stations={stations} onStationsChanged={load} />
      ) : (
      <>

      <div className="mb-3 grid grid-cols-2 gap-3 rounded-lg border border-gray-700 bg-gray-800/40 p-3 md:grid-cols-4 xl:grid-cols-8">
        <SearchInput
          className="col-span-2 md:col-span-4 xl:col-span-2"
          label="Search"
          value={table.queryInput}
          onChange={table.setQueryInput}
          placeholder="Id, name, contact, organization, email…"
        />
        <FilterSelect
          label="Ingest"
          value={filters.ingest}
          onChange={(v) => setFilter("ingest", v)}
          options={[
            { value: "all", label: "All" },
            { value: "push", label: "Push" },
            { value: "pull", label: "Pull" },
          ]}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(v) => setFilter("status", v)}
          options={[
            { value: "all", label: "All" },
            { value: "active", label: "Active" },
            { value: "pending", label: "Pending" },
            { value: "revoked", label: "Revoked" },
          ]}
        />
        <FilterSelect
          label="Consent"
          value={filters.consent}
          onChange={(v) => setFilter("consent", v)}
          options={[
            { value: "all", label: "All" },
            { value: "owner", label: "By owner" },
            { value: "admin", label: "By admin" },
            { value: "none", label: "None" },
          ]}
        />
        <FilterSelect
          label="Provider"
          value={filters.provider}
          onChange={(v) => setFilter("provider", v)}
          options={[{ value: "all", label: "All" }, ...providers.map((p) => ({ value: p, label: p }))]}
        />
        <FilterSelect
          label="Type"
          value={filters.type}
          onChange={(v) => setFilter("type", v)}
          options={[{ value: "all", label: "All types" }, ...stationTypes.map((t) => ({ value: t, label: t }))]}
        />
        <FilterSelect
          label="Freshness"
          value={filters.fresh}
          onChange={(v) => setFilter("fresh", v)}
          options={[
            { value: "all", label: "All" },
            { value: "live", label: "Live" },
            { value: "stale", label: "Stale" },
            { value: "nodata", label: "No data" },
          ]}
        />
      </div>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-300">
        <span aria-live="polite">
          {loading ? "Loading stations…" : `${total} of ${rows.length} stations`}
          {liveError && (
            <span className="ml-2 text-amber-300" title={liveError}>
              · live values unavailable
            </span>
          )}
        </span>
        {table.activeFilterCount > 0 && (
          <button type="button" onClick={table.resetFilters} className="text-cyan-400 hover:text-cyan-300">
            Clear filters ({table.activeFilterCount})
          </button>
        )}
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-700 bg-red-900/40 p-3 text-red-200">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="min-w-full divide-y divide-gray-700 bg-gray-800/60 text-sm">
          <thead className="bg-gray-900">
            <tr>
              <SortableHeader label="Station" sortKey="name" sort={sort} onSort={table.toggleSort} />
              <SortableHeader label="Ingest" sortKey="ingest" sort={sort} onSort={table.toggleSort} />
              <SortableHeader label="Live wind" sortKey="wind" sort={sort} onSort={table.toggleSort} />
              <SortableHeader label="Last obs" sortKey="obs" sort={sort} onSort={table.toggleSort} />
              <SortableHeader label="Consent" sortKey="consent" sort={sort} onSort={table.toggleSort} />
              <SortableHeader label="Status" sortKey="status" sort={sort} onSort={table.toggleSort} />
              <th scope="col" className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-300">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/70 text-gray-200">
            {loading ? (
              <TableSkeleton cols={colSpan} rows={6} />
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-gray-400">
                  {stations.length === 0 ? (
                    "No stations registered yet."
                  ) : view === "review" && pendingCount === 0 ? (
                    "Nothing waiting for review."
                  ) : (
                    <>
                      No stations match the filters.{" "}
                      <button type="button" onClick={table.resetFilters} className="text-cyan-400 hover:text-cyan-300">
                        Clear filters
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              pageRows.map((r) => {
                const c = r.c;
                const test = pullTests[c.stationId];
                return (
                  <tr
                    key={c.stationId}
                    onClick={() => setSelectedId(c.stationId)}
                    className={`cursor-pointer align-top hover:bg-gray-700/40 ${
                      selectedId === c.stationId ? "bg-gray-700/60" : ""
                    }`}
                  >
                    <td className="px-3 py-2.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(c.stationId);
                        }}
                        className="rounded text-left font-medium text-white hover:text-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
                      >
                        {c.name || c.stationId}
                      </button>
                      <div className="font-mono text-xs text-gray-400">{c.stationId}</div>
                      {(c.contact?.organization || c.contact?.stationType) && (
                        <div className="text-xs text-gray-400">
                          {[c.contact?.organization, c.contact?.stationType].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-gray-300">
                      {c.contact?.pollUrl ? (
                        <div>
                          <div className="text-white">pull</div>
                          <div className="text-xs">
                            {c.contact.pollProvider ?? "?"} ·{" "}
                            {c.contact.pollIntervalS ?? "?"}s
                          </div>
                          {/* Pending self-service pull request: what the reviewer approves.
                              The URL is masked — its query string may carry a secret. */}
                          {c.status === "pending" && (
                            <div data-testid={`pending-pull-${c.stationId}`} className="mt-1 max-w-xs space-y-0.5 text-xs">
                              <div className="break-all font-mono text-gray-300" title="Query string hidden">
                                {maskUrl(c.contact.pollUrl)}
                              </div>
                              {c.contact.pollOptions && (
                                <div className="break-words text-gray-400">
                                  {summarizeMapping(c.contact.pollOptions)}
                                </div>
                              )}
                              {test?.running && <div className="text-gray-300">Testing…</div>}
                              {test?.result && !test.running && (
                                <div className={test.result.ok ? "text-green-300" : "text-red-300"}>
                                  {test.result.ok
                                    ? `Test OK — ${
                                        test.result.sample?.windMs != null
                                          ? `${(test.result.sample.windMs * MS_TO_KNOTS).toFixed(1)} kn`
                                          : "parsed"
                                      }`
                                    : `Test failed — ${describeErrorCode(test.result.error) || "no usable data"}`}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs">push</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">
                      {r.live?.windKts != null ? (
                        <>
                          <span className="text-white">{fmtKts(r.live.windKts)}</span>
                          <span className="text-gray-400"> / {fmtKts(r.live.gustKts)} kts</span>
                          <span className="ml-1 text-gray-400">{fmtDir(r.live.windDir)}</span>
                        </>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="text-gray-300">{fmtLastObs(r.live?.obsTs)}</div>
                      <FreshnessBadge freshness={r.freshness} />
                    </td>
                    {/* Consent must be visible without opening anything: publishing a
                        station with none on record is the thing to catch here. */}
                    <td className="px-3 py-2.5">
                      {r.consent === "none" ? (
                        <span className="text-xs font-medium text-amber-300">none</span>
                      ) : (
                        <div>
                          <div className="text-green-300">{r.consent === "admin" ? "by admin" : "by owner"}</div>
                          <div className="text-xs text-gray-400">{c.contact?.dataConsentVersion}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <StationStatusBadge status={c.status} />
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <ActionsMenu label={`Actions for ${c.stationId}`} items={actionsFor(c)} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {!loading && total > 0 && (
        <Pagination
          darkMode
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          limit={table.pageSize}
          limitOptions={MANAGE_PAGE_SIZES}
          onPageChange={table.setPage}
          onLimitChange={table.setPageSize}
        />
      )}
      </>
      )}

      {selected && view !== "rejects" && (
        <ManageDetailPanel
          row={selected}
          onClose={() => setSelectedId(null)}
          onSettings={() => setEditTarget(selected.c)}
          onOpenRejects={(q) => {
            setSelectedId(null);
            setView("rejects", q);
          }}
        />
      )}

      {createOpen && (
        <CreateStationModal
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
          onSwitchToPull={() => {
            setCreateOpen(false);
            setPullWizard({ station: null });
          }}
        />
      )}
      {pullWizard && (
        <PullStationWizard
          station={pullWizard.station}
          onClose={() => setPullWizard(null)}
          onSaved={handlePullSaved}
        />
      )}
      {tokenOnce && (
        <TokenOnceModal
          token={tokenOnce}
          graceUntil={tokenOnceGraceUntil}
          onClose={() => {
            setTokenOnce(null);
            setTokenOnceGraceUntil(null);
          }}
        />
      )}
      {editTarget && (
        <StationSettingsModal
          station={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            load();
          }}
          onEditPull={() => {
            const target = editTarget;
            setEditTarget(null);
            setPullWizard({ station: target });
          }}
        />
      )}
      {credentialTarget && (
        <CredentialModal
          station={credentialTarget}
          graceUntil={
            Math.max(credentialTarget.graceUntil ?? 0, rotatedUntil[credentialTarget.stationId] ?? 0) || null
          }
          onClose={() => setCredentialTarget(null)}
          onReplaced={(token, graceUntil) => {
            const stationId = credentialTarget.stationId;
            setCredentialTarget(null);
            if (graceUntil) {
              setRotatedUntil((prev) => ({ ...prev, [stationId]: graceUntil }));
            }
            if (token) {
              setTokenOnceGraceUntil(graceUntil);
              setTokenOnce(token);
            }
            load();
          }}
        />
      )}
    </div>
  );
};

/** Contact, consent, credential and pull details — kept out of the table to keep rows compact. */
/**
 * Rejects that may belong to a station (WU ID / Windguru uid hint naming it, or its current
 * prefix with a wrong token). Best effort: fetched when the panel opens; a failure shows nothing.
 */
const StationRejectsSection: React.FC<{ station: PushStationCredential; onOpen: (q: string) => void }> = ({
  station,
  onOpen,
}) => {
  const [rejects, setRejects] = useState<PushReject[] | null>(null);
  useEffect(() => {
    let alive = true;
    PushStationService.fetchRejects()
      .then((res) => alive && setRejects(res.rejects.filter((r) => rejectMatchesStation(r, station))))
      .catch(() => alive && setRejects([]));
    return () => {
      alive = false;
    };
  }, [station]);
  if (!rejects || rejects.length === 0) return null;
  const total = rejects.reduce((n, r) => n + r.count, 0);
  const last = Math.max(...rejects.map((r) => r.lastSeen));
  const hint = rejects.map(stationHintOf).find(Boolean);
  return (
    <DetailSection title="Rejected pushes that may belong to this station">
      <DetailRow label="Rejects">
        {total} from {new Set(rejects.map((r) => r.ip)).size} IP(s), last {fmtAgo(last)}
      </DetailRow>
      <DetailRow label="Token prefixes">
        {Array.from(new Set(rejects.map((r) => r.tokenPrefix ?? "none"))).join(", ")}
      </DetailRow>
      <button
        type="button"
        onClick={() => onOpen(hint ?? station.prefix ?? station.stationId)}
        className="mt-1 text-sm text-cyan-400 hover:text-cyan-300"
      >
        Open in Rejected pushes →
      </button>
    </DetailSection>
  );
};

const ManageDetailPanel: React.FC<{
  row: ManageRow;
  onClose: () => void;
  onSettings: () => void;
  onOpenRejects: (query: string) => void;
}> = ({ row, onClose, onSettings, onOpenRejects }) => {
  const c = row.c;
  const ct = c.contact ?? {};
  return (
    <SidePanel
      title={c.name || c.stationId}
      subtitle={c.stationId}
      onClose={onClose}
      footer={
        <button
          type="button"
          onClick={onSettings}
          className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500"
        >
          Open settings
        </button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <StationStatusBadge status={c.status} />
        <SourceBadge source={row.provider} />
        <FreshnessBadge freshness={row.freshness} />
      </div>
      {!row.isPull && <StationRejectsSection station={c} onOpen={onOpenRejects} />}
      <DetailSection title="Live (Cloudflare)">
        <DetailRow label="Snapshot id">{canonicalIdOf(c)}</DetailRow>
        <DetailRow label="Wind / gust">
          {row.live?.windKts != null ? `${fmtKts(row.live.windKts)} / ${fmtKts(row.live.gustKts)} kts` : "—"}
        </DetailRow>
        <DetailRow label="Direction">{fmtDir(row.live?.windDir)}</DetailRow>
        <DetailRow label="Last obs">{fmtLastObs(row.live?.obsTs)}</DetailRow>
      </DetailSection>
      <DetailSection title="Station">
        <DetailRow label="Position">
          {c.lat?.toFixed(4)}, {c.lon?.toFixed(4)}
        </DetailRow>
        <DetailRow label="Type">{ct.stationType || "—"}</DetailRow>
        <DetailRow label="Hardware">{ct.hardware || "—"}</DetailRow>
        {ct.mastHeightM != null && <DetailRow label="Mast">{ct.mastHeightM} m</DetailRow>}
        {ct.spotId != null && <DetailRow label="Surfr spot">{ct.spotId}</DetailRow>}
      </DetailSection>
      <DetailSection title="Ingest">
        {row.isPull ? (
          <>
            <DetailRow label="Provider">{ct.pollProvider ?? "?"}</DetailRow>
            <DetailRow label="Interval">{ct.pollIntervalS ?? "?"} s</DetailRow>
            {ct.pollUrl && <DetailRow label="URL">{maskUrl(ct.pollUrl)}</DetailRow>}
            {ct.pollExternalId && <DetailRow label="External id">{ct.pollExternalId}</DetailRow>}
            {ct.pollOptions && <DetailRow label="Mapping">{summarizeMapping(ct.pollOptions)}</DetailRow>}
            <DetailRow label="Upstream token">{ct.pollAuthTokenConfigured ? "configured" : "none"}</DetailRow>
          </>
        ) : (
          <DetailRow label="Mode">push</DetailRow>
        )}
        <DetailRow label="Token prefix">{c.prefix ?? "— (no push credential)"}</DetailRow>
        <DetailRow label="Rate limit">{c.ratePerMin ?? 60} / min</DetailRow>
      </DetailSection>
      <DetailSection title="Contact">
        <DetailRow label="Name">{ct.contactName || "—"}</DetailRow>
        <DetailRow label="Organization">
          {ct.organization || "—"}
          {ct.organizationType ? ` (${ct.organizationType})` : ""}
        </DetailRow>
        <DetailRow label="Email">
          {ct.email ? (
            <a href={`mailto:${ct.email}`} className="text-cyan-400 hover:text-cyan-300">
              {ct.email}
            </a>
          ) : (
            "—"
          )}
        </DetailRow>
        {ct.phone && <DetailRow label="Phone">{ct.phone}</DetailRow>}
        {ct.website && <DetailRow label="Website">{ct.website}</DetailRow>}
        {ct.address && <DetailRow label="Address">{ct.address}</DetailRow>}
        {ct.ownerUserId != null && <DetailRow label="Owner user">{ct.ownerUserId}</DetailRow>}
      </DetailSection>
      <DetailSection title="Consent">
        {row.consent === "none" ? (
          <DetailRow label="Status">
            <span className="text-amber-300">none on record</span>
          </DetailRow>
        ) : (
          <>
            <DetailRow label="Status">{row.consent === "admin" ? "recorded by admin" : "accepted by owner"}</DetailRow>
            <DetailRow label="Version">{ct.dataConsentVersion}</DetailRow>
            <DetailRow label="At">{ct.dataConsentAt || "—"}</DetailRow>
            {ct.dataConsentActor && <DetailRow label="Actor">{ct.dataConsentActor}</DetailRow>}
          </>
        )}
      </DetailSection>
      {ct.notes && (
        <DetailSection title="Notes">
          <p className="whitespace-pre-wrap py-1.5 text-sm text-gray-200">{ct.notes}</p>
        </DetailSection>
      )}
    </SidePanel>
  );
};

export default PushStationsPage;
